import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Link2, Copy, Share2, Save, ExternalLink } from 'lucide-react';

const DIAS = [
  { value: 1, label: 'Lun' }, { value: 2, label: 'Mar' }, { value: 3, label: 'Mie' },
  { value: 4, label: 'Jue' }, { value: 5, label: 'Vie' }, { value: 6, label: 'Sab' }, { value: 0, label: 'Dom' },
];

export default function CarwashAgendaConfig() {
  const [config, setConfig] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    try { const res = await api.get('/carwash/agenda-config'); setConfig(res.data); }
    catch { toast.error('Error al cargar configuracion'); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    if (!config.slug?.trim()) { toast.error('El nombre de la liga es obligatorio'); return; }
    // Validaciones para evitar configs que muestran "no hay horarios"
    if (!config.dias_habiles || config.dias_habiles.length === 0) {
      toast.error('Selecciona al menos un dia habil'); return;
    }
    const [hi, mi] = (config.horario_inicio || '08:00').split(':').map(Number);
    const [hf, mf] = (config.horario_fin || '18:00').split(':').map(Number);
    if (hf * 60 + mf <= hi * 60 + mi) {
      toast.error('La hora de fin debe ser posterior a la hora de inicio'); return;
    }
    setGuardando(true);
    try {
      const res = await api.put('/carwash/agenda-config', config);
      setConfig(res.data);
      toast.success('Configuracion guardada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  const toggleDia = (dia) => {
    const dias = config.dias_habiles || [];
    setConfig({ ...config, dias_habiles: dias.includes(dia) ? dias.filter(d => d !== dia) : [...dias, dia] });
  };

  const copiarLiga = () => {
    const url = `${window.location.origin}/agenda/${config.slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Liga copiada al portapapeles');
  };

  const compartirWhatsApp = () => {
    const url = `${window.location.origin}/agenda/${config.slug}`;
    const msg = encodeURIComponent(`Agenda tu cita en linea: ${url}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  if (!config) return <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" /></div>;

  const ligaUrl = `${window.location.origin}/agenda/${config.slug}`;

  return (
    <div className="space-y-4" data-testid="agenda-config-page">
      <h2 className="text-lg font-bold text-slate-100 font-barlow flex items-center gap-2">
        <Link2 className="w-5 h-5 text-cyan-400" /> Liga de Agendamiento
      </h2>
      <p className="text-xs text-slate-500">
        Genera una liga unica para que tus clientes agenden citas desde WhatsApp o redes sociales.
      </p>

      <Card className="bg-slate-900/40 border-slate-800">
        <CardContent className="p-5 space-y-4">
          {/* Toggle activo */}
          <div className="flex items-center justify-between">
            <Label className="text-sm text-slate-300">Liga activa</Label>
            <button onClick={() => setConfig({ ...config, activo: !config.activo })}
              className={`w-12 h-6 rounded-full transition-all relative ${config.activo ? 'bg-cyan-500' : 'bg-slate-700'}`}
              data-testid="toggle-agenda-activa">
              <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-all ${config.activo ? 'left-6' : 'left-0.5'}`} />
            </button>
          </div>

          {/* Slug */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Nombre de la liga</Label>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-slate-600">/agenda/</span>
              <Input value={config.slug || ''} onChange={e => setConfig({ ...config, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                className="bg-slate-950 border-slate-700 text-slate-200 flex-1" data-testid="slug-input" />
            </div>
          </div>

          {/* Preview URL */}
          {config.slug && (
            <div className="bg-slate-800/50 rounded-sm p-3 flex items-center justify-between">
              <p className="text-xs text-cyan-400 truncate flex-1 font-mono">{ligaUrl}</p>
              <div className="flex gap-1 ml-2 shrink-0">
                <Button variant="ghost" size="sm" onClick={copiarLiga} className="text-slate-400 hover:text-cyan-400" data-testid="copiar-liga-btn">
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={compartirWhatsApp} className="text-slate-400 hover:text-emerald-400" data-testid="whatsapp-liga-btn">
                  <Share2 className="w-4 h-4" />
                </Button>
                <a href={ligaUrl} target="_blank" rel="noreferrer">
                  <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-200">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          )}

          {/* Schedule config */}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Hora inicio</Label>
              <Input type="time" value={config.horario_inicio || '08:00'} onChange={e => setConfig({ ...config, horario_inicio: e.target.value })}
                className="bg-slate-950 border-slate-700 text-slate-200 mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Hora fin</Label>
              <Input type="time" value={config.horario_fin || '18:00'} onChange={e => setConfig({ ...config, horario_fin: e.target.value })}
                className="bg-slate-950 border-slate-700 text-slate-200 mt-1" /></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Intervalo (min)</Label>
              <Input type="number" value={config.intervalo_min || 30} onChange={e => setConfig({ ...config, intervalo_min: parseInt(e.target.value) || 30 })}
                className="bg-slate-950 border-slate-700 text-slate-200 mt-1" min="15" max="120" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Max citas por horario</Label>
              <Input type="number" value={config.max_por_horario || 3} onChange={e => setConfig({ ...config, max_por_horario: parseInt(e.target.value) || 3 })}
                className="bg-slate-950 border-slate-700 text-slate-200 mt-1" min="1" max="50" data-testid="max-por-horario-input" /></div>
          </div>

          {/* Days */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Dias habiles</Label>
            <div className="flex gap-2">
              {DIAS.map(d => (
                <button key={d.value} onClick={() => toggleDia(d.value)}
                  className={`w-10 h-10 rounded-sm text-xs font-bold transition-all ${
                    (config.dias_habiles || []).includes(d.value) ? 'bg-cyan-500 text-black' : 'bg-slate-800 text-slate-500'
                  }`} data-testid={`dia-habil-${d.value}`}>{d.label}</button>
              ))}
            </div>
            {(!config.dias_habiles || config.dias_habiles.length === 0) && (
              <p className="text-[11px] text-red-400 mt-2" data-testid="dias-vacios-warning">
                Selecciona al menos un dia; de lo contrario los clientes no veran horarios disponibles.
              </p>
            )}
          </div>

          {/* Confirmation message */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-400">Mensaje de confirmacion</Label>
            <Input value={config.mensaje_confirmacion || ''} onChange={e => setConfig({ ...config, mensaje_confirmacion: e.target.value })}
              className="bg-slate-950 border-slate-700 text-slate-200 mt-1" />
          </div>

          <Button onClick={guardar} disabled={guardando}
            className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm" data-testid="guardar-agenda-config-btn">
            <Save className="w-4 h-4 mr-2" /> {guardando ? 'Guardando...' : 'Guardar Configuracion'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
